import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";

import { useUser } from "@/lib/useUser";
import { useAvailableCredits } from "@/hooks/useAvailableCredits";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import CreditDisplay from "@/components/credits/CreditDisplay";
import LowCreditsBanner from "@/components/credits/LowCreditsBanner";
import AlphaBanner from "@/components/AlphaBanner";
import {
  LayoutDashboard,
  Wand2,
  Store,
  Coins,
  Share2,
  BarChart3,
  Settings,
  HelpCircle,
  Menu,
  X,
  LogOut,
  User as UserIcon,
  Shield,
  RectangleEllipsis, // Added for Thumbnails
  Video, // Added for Generate Videos
  Mic, // Added Mic icon
  FolderOpen,
  Paintbrush,
  Palette,
  LayoutTemplate,
  UserCircle,
  Target,
  MessageSquare,
  Layers,
  Users,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner"; // Import Toaster
import AccessDenied from "@/components/AccessDenied";
import { flags } from "@/lib/flags";
import AssistantPanel from "@/components/assistant/AssistantPanel";
import AssistantLauncher from "@/components/assistant/AssistantLauncher";

const SIDEBAR_COLLAPSED_KEY = "nimblenode_sidebar_collapsed";

const navigationItems = [
  // Creation
  { title: "Dashboard", url: "Dashboard", icon: LayoutDashboard, group: "create" },
  { title: "Create Images", url: "Generate", icon: Wand2, group: "create" },
  { title: "Create Videos", url: "GenerateVideos", icon: Video, group: "create" },
  { title: "Create Thumbnails", url: "GenerateThumbnails", icon: RectangleEllipsis, group: "create" },
  { title: "Carousel", url: "Carousel", icon: Layers, group: "create" },
  { title: "Create Voiceover", url: "GenerateAudio", icon: Mic, group: "create" },
{ title: "Design Studio", url: "DesignEditor", icon: Palette, group: "create" },
  { title: "AI Avatars", url: "AIAvatars", icon: UserCircle, group: "create" },
  // Content
  { title: "My Assets", url: "MyAssets", icon: FolderOpen, group: "content" },
  { title: "Content Calendar", url: "ContentCalendar", icon: CalendarDays, group: "content" },
  { title: "Templates", url: "Templates", icon: LayoutTemplate, group: "content" },
  { title: "Marketplace", url: "Marketplace", icon: Store, group: "content" },
  // Business
  { title: "Credits", url: "Credits", icon: Coins, group: "business" },
  { title: "Seller Dashboard", url: "SellerDashboard", icon: BarChart3, group: "business" },
  { title: "Social Hub", url: "SocialHub", icon: Share2, group: "business" },
  { title: "Competitors", url: "CompetitorAnalysis", icon: Target, group: "business" },
  // Account
  { title: "Brand Kit", url: "BrandKit", icon: Paintbrush, group: "account" },
  { title: "Analytics", url: "Analytics", icon: BarChart3, group: "account" },
  { title: "Settings", url: "Settings", icon: Settings, group: "account" },
  { title: "Team", url: "Team", icon: Users, group: "account" },
  { title: "Help", url: "Help", icon: HelpCircle, group: "account" },
  { title: "Feedback", url: "Feedback", icon: MessageSquare, group: "account" },
  // { title: "Report a Bug", url: "ReportBug", icon: Bug, group: "account" }, // hidden during alpha; restore for beta
];

const developerNavigation = [
  { title: "Dev Portal", url: "DevPortal", icon: Shield },
  { title: "Admin Dashboard", url: "AdminDashboard", icon: LayoutDashboard },
];

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: loading, isAuthenticated, invalidateUser } = useUser();
  const { isAdmin } = useAuth();
  // Org-aware credit balance — same hook used by generation pages and Credits
  // page, so the sidebar banner always matches what the user actually sees.
  const availableCredits = useAvailableCredits();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // localStorage throws SecurityError in Safari Private Mode / when cookies are
    // blocked. This runs in the app shell (above PageErrorBoundary), so an
    // unguarded throw here white-screens the whole app.
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"; }
    catch { return false; }
  });
  const [assistantOpen, setAssistantOpen] = useState(false);
  const showGuide = flags.guideAssistant();

  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch { /* storage blocked */ }
      return next;
    });
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  // Close the mobile overlay on browser back/forward navigation.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    // Also clear dev portal token on main logout
    localStorage.removeItem("dev_portal_token");
    base44.auth.logout();
    invalidateUser();
    navigate(createPageUrl("Landing"));
  };

  const getTierColor = (tier) => {
    const colors = {
      free: "bg-[#334155]",
      starter: "bg-[#06B6D4]",
      creator: "bg-[#06B6D4]",
      professional: "bg-[#10B981]",
      business: "bg-[#F59E0B]",
      enterprise: "bg-[#EF4444]"
    };
    return colors[tier] || colors.free;
  };

  // Public / pre-login pages render without the main app layout and must NOT
  // be blocked by the auth loading spinner. The spinner gate below is reached
  // only for protected routes. This order prevents the useUser() background
  // refetch (every 30s) from flipping isLoading true on logged-out visitors
  // and remounting the Landing page at scroll position 0.
  if (currentPageName === "Landing" || currentPageName === "DevLogin") {
    return children;
  }

  // AcceptInvite renders its own full-page UI and handles needs_auth itself
  if (currentPageName === "AcceptInvite") {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#06B6D4]"></div>
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  // Render main app layout for authenticated users
  if (isAuthenticated && user) {
    return (
      <>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded focus:shadow-lg">
          Skip to main content
        </a>
        <TooltipProvider delayDuration={200}>
          <div className="min-h-screen bg-[#0F172A] text-white">
            <div className="flex">
              {/* Sidebar */}
              <aside
                id="main-sidebar"
                aria-label="Main navigation"
                className={`fixed inset-y-0 left-0 z-50 glass transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
                  sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } lg:static lg:inset-0 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}
              >
                <div className="flex flex-col h-full">
                  {/* Logo + Collapse Toggle */}
                  <div className={`border-b border-[#334155] flex items-center ${sidebarCollapsed ? 'flex-col gap-2 py-4 px-2' : 'p-4 justify-between'}`}>
                    {!sidebarCollapsed && (
                      <Link to={createPageUrl("Dashboard")} className="logo-wrap flex-1 flex justify-center">
                        <img
                          src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/7fe2487c5_FINALNimbleNodeLogov11.png"
                          alt="NimbleNode Logo"
                          className="h-14"
                          width="auto"
                          height="56"
                        />
                      </Link>
                    )}
                    {sidebarCollapsed && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={createPageUrl("Dashboard")}
                            className="flex items-center justify-center w-10 h-10 rounded-xl text-[#94A3B8] hover:text-white hover:bg-[#334155]/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A]"
                            aria-label="Dashboard"
                          >
                            <LayoutDashboard className="w-5 h-5" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="bg-[#1E293B] text-white border-[#334155]">
                          Dashboard
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleCollapsed}
                      aria-expanded={!sidebarCollapsed}
                      aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                      className="text-[#94A3B8] hover:text-white hover:bg-[#334155]/50 flex-shrink-0 focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A]"
                    >
                      {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </Button>
                  </div>

                  {/* Navigation */}
                  <nav aria-label="Page navigation" className={`flex-1 overflow-y-auto ${sidebarCollapsed ? 'p-2 space-y-1' : 'p-4 space-y-2'}`}>
                    {navigationItems.filter(item => !item.hidden && (!item.adminOnly || isAdmin)).map((item, index, filteredItems) => (
                      <React.Fragment key={item.title}>
                        {index > 0 && item.group !== filteredItems[index - 1].group && (
                          <div className="border-t border-white/10 my-2"></div>
                        )}
                        {sidebarCollapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Link
                                to={createPageUrl(item.url)}
                                aria-current={location.pathname === createPageUrl(item.url) ? 'page' : undefined}
                                aria-label={item.title}
                                className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] ${
                                  location.pathname === createPageUrl(item.url)
                                    ? 'bg-[#06B6D4] text-white'
                                    : item.accent
                                      ? 'bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-violet-300 hover:text-white border border-violet-500/30'
                                      : 'text-[#94A3B8] hover:text-white hover:bg-[#334155]/50'
                                }`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <item.icon className="w-5 h-5" />
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="bg-[#1E293B] text-white border-[#334155]">
                              {item.title}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Link
                            to={createPageUrl(item.url)}
                            aria-current={location.pathname === createPageUrl(item.url) ? 'page' : undefined}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] ${
                              location.pathname === createPageUrl(item.url)
                                ? 'bg-[#06B6D4] text-white'
                                : item.accent
                                  ? 'bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-violet-300 hover:text-white hover:from-violet-500/30 hover:to-fuchsia-500/30 border border-violet-500/30'
                                  : 'text-[#94A3B8] hover:text-white hover:bg-[#334155]/50'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.title}</span>
                          </Link>
                        )}
                      </React.Fragment>
                    ))}

                    {/* Developer Portal — admin only */}
                    {isAdmin && (
                      <>
                        <div className="border-t border-white/10 my-4"></div>
                        {developerNavigation.map((item) => (
                          sidebarCollapsed ? (
                            <Tooltip key={item.title}>
                              <TooltipTrigger asChild>
                                <Link
                                  to={createPageUrl(item.url)}
                                  aria-current={location.pathname === createPageUrl(item.url) ? 'page' : undefined}
                                  aria-label={item.title}
                                  className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] ${
                                    location.pathname === createPageUrl(item.url) ? 'bg-[#F59E0B]/20 text-white' : ''
                                  }`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  <item.icon className="w-5 h-5" />
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="bg-[#1E293B] text-white border-[#334155]">
                                {item.title}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Link
                              key={item.title}
                              to={createPageUrl(item.url)}
                              aria-current={location.pathname === createPageUrl(item.url) ? 'page' : undefined}
                              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] ${
                                location.pathname === createPageUrl(item.url) ? 'bg-[#F59E0B]/20 text-white' : ''
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon className="w-5 h-5" />
                              <span className="font-medium">{item.title}</span>
                            </Link>
                          )
                        ))}
                      </>
                    )}
                  </nav>

                  {/* User Profile */}
                  <div className={`border-t border-[#334155] ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
                    {sidebarCollapsed ? (
                      <Tooltip>
                        <DropdownMenu>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`User menu — ${user.full_name || user.username}`}
                                className="w-10 h-10 text-[#94A3B8] hover:text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] relative"
                              >
                                <Avatar className="w-8 h-8">
                                  <AvatarFallback className={`${getTierColor(user.tier)} text-white font-semibold text-xs`}>
                                    {user.full_name?.charAt(0) || user.username?.charAt(0) || 'U'}
                                  </AvatarFallback>
                                </Avatar>
                                {/* Amber dot when credits are low and sidebar is collapsed.
                                    Guard isLoading to avoid a false positive while the org
                                    balance is still in flight (would show 0 ≤ threshold). */}
                                {!availableCredits.isLoading && availableCredits.total <= (user.low_credit_threshold ?? 50) && (
                                  <span
                                    className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-[#0F172A]"
                                    aria-label="Low credits"
                                    role="status"
                                  />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                            <DropdownMenuContent side="right" align="end" className="w-48 glass border-[#334155] text-white">
                              <DropdownMenuItem onClick={() => navigate(createPageUrl("Settings"))} className="focus:bg-white/10 focus:text-white cursor-pointer">
                                <Settings className="w-4 h-4 mr-2" />
                                Settings
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#334155]" />
                              <DropdownMenuItem
                                onClick={handleLogout}
                                className="text-[#EF4444] focus:bg-[#EF4444]/10 focus:text-[#EF4444]"
                              >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipContent side="right" className="bg-[#1E293B] text-white border-[#334155]">
                          {user.full_name || user.username} — {user.tier} Plan
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className={`${getTierColor(user.tier)} text-white font-semibold`}>
                              {user.full_name?.charAt(0) || user.username?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {user.full_name || user.username}
                            </p>
                            <p className="text-xs text-[#94A3B8] capitalize">{user.tier} Plan</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="User menu" className="text-[#94A3B8] hover:text-white hover:bg-white/10">
                                <UserIcon className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-48 glass border-[#334155] text-white">
                              <DropdownMenuItem onClick={() => navigate(createPageUrl("Settings"))} className="focus:bg-white/10 focus:text-white cursor-pointer">
                                <Settings className="w-4 h-4 mr-2" />
                                Settings
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#334155]" />
                              <DropdownMenuItem
                                onClick={handleLogout}
                                className="text-[#EF4444] focus:bg-[#EF4444]/10 focus:text-[#EF4444]"
                              >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        {/* Credits Display */}
                        <CreditDisplay user={user} variant="sidebar" />
                        <LowCreditsBanner
                          totalCredits={availableCredits.total}
                          threshold={user.low_credit_threshold ?? 50}
                        />
                      </>
                    )}
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 flex flex-col min-w-0">
                <AlphaBanner />
                {/* Mobile Header */}
                <header className="lg:hidden glass border-b border-[#334155] p-4 flex items-center justify-between">
                  <Link to={createPageUrl("Dashboard")} className="logo-wrap">
                    <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/7fe2487c5_FINALNimbleNodeLogov11.png" alt="NimbleNode Logo" className="h-11" loading="lazy" width="auto" height="44" />
                  </Link>
                  <div className="flex items-center gap-3">
                    <CreditDisplay user={user} variant="header" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSidebarOpen(!sidebarOpen)}
                      aria-label="Toggle navigation menu"
                      aria-expanded={sidebarOpen}
                      aria-controls="main-sidebar"
                      className="text-white hover:bg-white/10"
                    >
                      {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </Button>
                  </div>
                </header>
                <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto">
                  {children}
                </main>
              </div>

              {/* Mobile Overlay */}
              {sidebarOpen && (
                <div
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  role="button"
                  tabIndex={0}
                  aria-label="Close navigation menu"
                  onClick={() => setSidebarOpen(false)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setSidebarOpen(false); }}
                />
              )}
            </div>
          </div>
          {showGuide && (
            <>
              <AssistantLauncher
                onClick={() => setAssistantOpen(v => !v)}
                isOpen={assistantOpen}
              />
              <AssistantPanel
                isOpen={assistantOpen}
                onClose={() => setAssistantOpen(false)}
                currentPageName={currentPageName}
              />
            </>
          )}
        </TooltipProvider>
        <Toaster theme="dark" position="bottom-right" />
      </>
    );
  }

  // Fallback for unauthenticated users trying to access protected pages
  return <AccessDenied />;
}